import type {
  ExcelColumnSpec,
  ExcelFinding
} from 'components/shared/ExcelImport';
import type {
  HoursBalanceImportFinding,
  HoursBalanceImportRow,
  HoursBalanceImportValidateRow
} from 'generated';
import type {
  HoursBalanceExcelRow,
  HoursBalanceFileWideIssue
} from './HoursBalanceImport.types';

export const BLOCK_SIZE = 250;
export const MAX_ROWS = 4000;

// Positional, per the report layout - the header is only a secondary check.
const COMMON_PREFIX: ExcelColumnSpec[] = [
  {
    header: 'Medewerkernummer',
    type: 'number',
    key: 'employeeNr',
    required: true
  },
  { header: 'Medewerkernaam', type: 'skip' },
  {
    header: 'Dienstverbandvolgnummer',
    type: 'number',
    key: 'workAgreementNr',
    required: true
  },
  { header: 'PIS-registratienummer', type: 'skip' },
  { header: 'Invoer JUS', type: 'number', key: 'inputJus' },
  { header: 'Correctie uren', type: 'number', key: 'inputWorkingHoursCor' },
  { header: 'Correctie LL uren', type: 'number', key: 'inputHoursLcs' },
  {
    header: 'LL uren naar pensioenfonds',
    type: 'number',
    key: 'hoursLcsUsedFin'
  },
  {
    header: 'LL uren naar levensloop',
    type: 'number',
    key: 'hoursLcsUsedLife'
  },
  {
    header: 'Wettelijk correctie verlof-uren',
    type: 'number',
    key: 'inputHoursLeaveMin'
  },
  {
    header: 'Wettelijk vorig jaar',
    type: 'number',
    key: 'inputHoursLeaveMinPrev'
  },
  {
    header: 'Bovenwettelijk correctie verlof-uren',
    type: 'number',
    key: 'inputHoursLeaveOver'
  },
  {
    header: 'Bovenwettelijk eenmalige storting naar rato',
    type: 'number',
    key: 'leaveOneTimeDeposit'
  },
  {
    header: 'Bovenwettelijk vorig jaar',
    type: 'number',
    key: 'inputHoursLeaveOverPrev'
  },
  { header: '1+ jaar oud', type: 'number', key: 'inputHoursLeave1Cor' },
  { header: '2+ jaar oud', type: 'number', key: 'inputHoursLeave2Cor' },
  { header: '3+ jaar oud', type: 'number', key: 'inputHoursLeave3Cor' },
  { header: '4+ jaar oud', type: 'number', key: 'inputHoursLeave4Cor' },
  { header: '5+ jaar oud', type: 'number', key: 'inputHoursLeave5Cor' },
  {
    header: 'Leeftijdsurencompensatie',
    type: 'number',
    key: 'inputHoursAgeCor'
  },
  { header: 'PLB Huidig', type: 'number', key: 'inputHoursPlbCor' },
  {
    header: 'PLB eenmalige storting naar rato',
    type: 'number',
    key: 'plbOneTimeDeposit'
  },
  { header: 'PLB vorig jaar', type: 'number', key: 'inputHoursPlbPrevCor' },
  { header: 'PLB 1+ jaar oud', type: 'number', key: 'inputHoursPlb1Cor' },
  { header: 'PLB 2+ jaar oud', type: 'number', key: 'inputHoursPlb2Cor' },
  { header: 'PLB 3+ jaar oud', type: 'number', key: 'inputHoursPlb3Cor' },
  { header: 'PLB 4+ jaar oud', type: 'number', key: 'inputHoursPlb4Cor' },
  { header: 'PLB 5+ jaar oud', type: 'number', key: 'inputHoursPlb5Cor' }
];

const PLB_6_TO_10: ExcelColumnSpec[] = [6, 7, 8, 9, 10].map((year) => ({
  header: `PLB ${year}+ jaar oud`,
  type: 'number',
  key: `inputHoursPlb${year}Cor`
}));

const COMMON_SUFFIX: ExcelColumnSpec[] = [
  {
    header: 'Opname PLB uren naar urensaldo',
    type: 'number',
    key: 'hoursPlbUsedWork'
  },
  {
    header: 'Opname PLB uren naar verlofsaldo',
    type: 'number',
    key: 'hoursPlbUsedLv'
  },
  {
    header: 'Opname PLB uren naar levensloop',
    type: 'number',
    key: 'hoursPlbUsedLcs'
  },
  { header: 'Inleesmaand', type: 'date', key: 'month', required: true },
  { header: 'Memo', type: 'text', key: 'inputWorkingMemo' }
];

const SUFFIX_WITHOUT_MEMO: ExcelColumnSpec[] = COMMON_SUFFIX.filter(
  (column) => column.key !== 'inputWorkingMemo'
);

// 38 columns: the current template.
export const HOURS_BALANCE_COLUMNS_WITH_PLB_6_10: ExcelColumnSpec[] = [
  ...COMMON_PREFIX,
  ...PLB_6_TO_10,
  ...COMMON_SUFFIX
];

// 32 columns: the released template, which predates both PLB 6-10 and Memo -
// the file a customer who hasn't regenerated their template still has on disk.
export const HOURS_BALANCE_COLUMNS_WITHOUT_PLB_6_10: ExcelColumnSpec[] = [
  ...COMMON_PREFIX,
  ...SUFFIX_WITHOUT_MEMO
];

// 37 columns: PLB 6-10 but no Memo yet, only ever generated on test/acceptance
// from the intermediate build of this release train.
export const HOURS_BALANCE_COLUMNS_WITHOUT_MEMO: ExcelColumnSpec[] = [
  ...COMMON_PREFIX,
  ...PLB_6_TO_10,
  ...SUFFIX_WITHOUT_MEMO
];

// Every layout the report has actually produced is valid input; the column count
// tells them apart unambiguously.
export const resolveHoursBalanceColumns = (
  columnCount: number
): ExcelColumnSpec[] | null =>
  [
    HOURS_BALANCE_COLUMNS_WITH_PLB_6_10,
    HOURS_BALANCE_COLUMNS_WITHOUT_MEMO,
    HOURS_BALANCE_COLUMNS_WITHOUT_PLB_6_10
  ].find((columns) => columns.length === columnCount) ?? null;

// Derived from the column spec rather than duplicated, so a future column never
// silently falls out of sync between the two.
export const NUMERIC_FIELD_KEYS = HOURS_BALANCE_COLUMNS_WITH_PLB_6_10.filter(
  (column) =>
    column.type === 'number' &&
    column.key &&
    column.key !== 'employeeNr' &&
    column.key !== 'workAgreementNr'
).map((column) => column.key as string);

export const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

export const toValidateRow = (
  row: HoursBalanceExcelRow
): HoursBalanceImportValidateRow => ({
  rowNumber: row.rowNumber,
  employeeNr: row.employeeNr,
  workAgreementNr: row.workAgreementNr
});

export const toExecuteRow = (
  row: HoursBalanceExcelRow,
  dutyLineMngId: number
): HoursBalanceImportRow => {
  const executeRow = { rowNumber: row.rowNumber, dutyLineMngId } as Record<
    string,
    number | string
  >;
  const rawRow = row as unknown as Record<string, number | string | undefined>;
  NUMERIC_FIELD_KEYS.forEach((key) => {
    const value = rawRow[key];
    if (typeof value === 'number') {
      executeRow[key] = value;
    }
  });
  // Left blank, this stays untouched - it doesn't follow emptyFieldHandling like the
  // numeric correction fields do.
  if (typeof rawRow.inputWorkingMemo === 'string') {
    executeRow.inputWorkingMemo = rawRow.inputWorkingMemo;
  }
  return executeRow as unknown as HoursBalanceImportRow;
};

// The server's finding shape allows a null column; the shared findings table only
// knows about a possibly-absent one.
export const toExcelFinding = (
  finding: HoursBalanceImportFinding
): ExcelFinding => ({
  rowNumber: finding.rowNumber,
  column: finding.column ?? undefined,
  expected: finding.expected,
  found: finding.found
});

// File-wide checks the shared ExcelImport component can't know about: it validates
// cells positionally, not the domain rule that one file is one month for one set of
// unique employee/work-agreement pairs.
export const findFileWideIssues = (
  rows: HoursBalanceExcelRow[]
): HoursBalanceFileWideIssue[] => {
  const issues: HoursBalanceFileWideIssue[] = [];
  const seenPairs = new Map<string, number>();
  let firstMonth: string | undefined;

  rows.forEach((row) => {
    const pairKey = `${row.employeeNr}-${row.workAgreementNr}`;
    const duplicateOfRow = seenPairs.get(pairKey);
    if (duplicateOfRow) {
      issues.push({
        rowNumber: row.rowNumber,
        kind: 'duplicate',
        duplicateOfRow
      });
    } else {
      seenPairs.set(pairKey, row.rowNumber);
    }

    if (firstMonth === undefined) {
      firstMonth = row.month;
    } else if (row.month !== firstMonth) {
      issues.push({ rowNumber: row.rowNumber, kind: 'month' });
    }
  });

  return issues;
};
